import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';
import { Events } from './components/Events';
import { Reports } from './components/Reports';
import { Navbar } from './components/Navbar';
import { api } from './services/api';
import notificationManager from './utils/notificationManager';

const App = () => {
  const [authStatus, setAuthStatus] = useState(
    !!localStorage.getItem('token')
  );
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [timePeriod, setTimePeriod] = useState('DAY');
  const latestEventId = useRef(null);
  const isInitialLoad = useRef(true);

  const getFromDate = (period) => {
    const now = new Date();
    switch (period) {
      case 'DAY':        { const d = new Date(now); d.setDate(now.getDate() - 1); return d.toISOString(); }
      case 'WEEK':       { const d = new Date(now); d.setDate(now.getDate() - 7); return d.toISOString(); }
      case 'MONTH':      { const d = new Date(now); d.setMonth(now.getMonth() - 1); return d.toISOString(); }
      case '3MONTHS':    { const d = new Date(now); d.setMonth(now.getMonth() - 3); return d.toISOString(); }
      default:           return null;
    }
  };

  const fetchData = useCallback(async (signal) => {
    if (!authStatus) return;

    try {
      setError(null);
      const eventsResponse = await api.getEvents(getFromDate(timePeriod), signal);

      // Extract events array from the response
      const eventsData = eventsResponse.events;

      // Check for new events by comparing the latest event ID
      if (eventsData && eventsData.length > 0) {
        const currentLatestEvent = eventsData[0]; // First event is the newest (sorted by timestamp desc)
        const currentLatestId = currentLatestEvent._id;

        // Get the previously stored latest event ID from localStorage
        const storedLatestId = localStorage.getItem('latestEventId');

        console.log('📊 Current latest event ID:', currentLatestId);
        console.log('📊 Stored latest event ID:', storedLatestId);
        console.log('📊 Is initial load:', isInitialLoad.current);

        // Check if there's a new event (different ID from the stored latest)
        if (!isInitialLoad.current && storedLatestId && currentLatestId !== storedLatestId) {
          console.log('🔔 NEW EVENT DETECTED!');
          console.log('New event:', currentLatestEvent.description, 'at', currentLatestEvent.timestamp);
          await notificationManager.notifyNewEvents(1);
        } else if (isInitialLoad.current) {
          console.log('⏭️ Initial load - storing latest event ID');
        } else {
          console.log('✅ No new events');
        }

        // Store the latest event ID in localStorage (persists across refreshes)
        localStorage.setItem('latestEventId', currentLatestId);
        isInitialLoad.current = false;
      }

      setEvents(eventsData);
    } catch (error) {
      if (error.name === 'AbortError') return; // cancelled — ignore
      console.error('Error fetching data:', error);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [authStatus, timePeriod]);

  // Fetch events data — abort previous request before starting a new one
  useEffect(() => {
    if (!authStatus) return;

    let currentController = new AbortController();
    setLoading(true);
    fetchData(currentController.signal);

    let interval;
    if (!selectedEventId) {
      interval = setInterval(() => {
        currentController.abort();
        currentController = new AbortController();
        fetchData(currentController.signal);
      }, 5000);
    }

    return () => {
      currentController.abort();
      if (interval) clearInterval(interval);
    };
  }, [authStatus, selectedEventId, fetchData]);

  return (
    <Router>
      <div className="min-h-screen bg-gray-100">
        <Navbar 
          isAuthenticated={authStatus} 
          setIsAuthenticated={setAuthStatus} 
        />
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mx-4 mt-4">
            {error}
          </div>
        )}

        <Routes>
          <Route 
            path="/login" 
            element={
              authStatus ? (
                <Navigate to="/events" />
              ) : (
                <Login setIsAuthenticated={setAuthStatus} />
              )
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <PrivateRoute isAuthenticated={authStatus}>
                <Dashboard
                  events={events}
                  loading={loading}
                />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/events" 
            element={
              <PrivateRoute isAuthenticated={authStatus}>
                <Events
                  events={events}
                  loading={loading}
                  onEventsUpdate={fetchData}
                  onEventSelect={setSelectedEventId}
                  selectedEventId={selectedEventId}
                  timePeriod={timePeriod}
                  onTimePeriodChange={setTimePeriod}
                />
              </PrivateRoute>
            } 
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute isAuthenticated={authStatus}>
                <Reports />
              </PrivateRoute>
            }
          />
          <Route
            path="/"
            element={<Navigate to="/dashboard" />}
          />
        </Routes>
      </div>
    </Router>
  );
};

const PrivateRoute = ({ children, isAuthenticated }) => {
  return isAuthenticated ? children : <Navigate to="/login" />;
};

export default App;